// Colored KPI tile row shared across every report page.
//
// Each tile follows the CLAUDE.md pattern: colored icon circle, tinted
// background, subtle radial glow, numeric value in tabular-nums. The row is
// responsive (1 → 2 → 3/4 columns).

import type { ReactNode } from 'react';

export type KpiTone = 'brand' | 'indigo' | 'violet' | 'pink' | 'teal' | 'success' | 'warning' | 'danger' | 'neutral';

export interface ReportKpiTile {
  id: string;
  title: string;
  value: string;
  subLabel?: string;
  trend?: {
    value: string;
    direction: 'up' | 'down' | 'flat';
  };
  icon: ReactNode;
  tone: KpiTone;
}

const TONE: Record<
  KpiTone,
  { tint: string; darkTint: string; iconBg: string; glow: string; accent: string }
> = {
  brand: {
    tint: 'bg-brand-50',
    darkTint: 'dark:bg-brand-900/20',
    iconBg: 'bg-gradient-to-br from-brand-500 to-brand-600',
    glow: 'bg-gradient-to-br from-brand-400 to-indigo-500',
    accent: 'text-brand-600 dark:text-brand-300',
  },
  indigo: {
    tint: 'bg-indigo-50',
    darkTint: 'dark:bg-indigo-900/20',
    iconBg: 'bg-gradient-to-br from-indigo-500 to-indigo-600',
    glow: 'bg-gradient-to-br from-indigo-400 to-violet-500',
    accent: 'text-indigo-600 dark:text-indigo-300',
  },
  violet: {
    tint: 'bg-violet-50',
    darkTint: 'dark:bg-violet-900/20',
    iconBg: 'bg-gradient-to-br from-violet-500 to-fuchsia-600',
    glow: 'bg-gradient-to-br from-violet-400 to-pink-500',
    accent: 'text-violet-600 dark:text-violet-300',
  },
  pink: {
    tint: 'bg-pink-50',
    darkTint: 'dark:bg-pink-900/20',
    iconBg: 'bg-gradient-to-br from-pink-500 to-rose-600',
    glow: 'bg-gradient-to-br from-pink-400 to-violet-500',
    accent: 'text-pink-600 dark:text-pink-300',
  },
  teal: {
    tint: 'bg-teal-50',
    darkTint: 'dark:bg-teal-900/20',
    iconBg: 'bg-gradient-to-br from-teal-500 to-teal-600',
    glow: 'bg-gradient-to-br from-teal-400 to-success-500',
    accent: 'text-teal-600 dark:text-teal-300',
  },
  success: {
    tint: 'bg-success-50',
    darkTint: 'dark:bg-success-900/20',
    iconBg: 'bg-gradient-to-br from-success-500 to-success-600',
    glow: 'bg-gradient-to-br from-success-400 to-teal-500',
    accent: 'text-success-600 dark:text-success-300',
  },
  warning: {
    tint: 'bg-warning-50',
    darkTint: 'dark:bg-warning-900/20',
    iconBg: 'bg-gradient-to-br from-warning-500 to-orange-600',
    glow: 'bg-gradient-to-br from-warning-400 to-danger-500',
    accent: 'text-warning-700 dark:text-warning-300',
  },
  danger: {
    tint: 'bg-danger-50',
    darkTint: 'dark:bg-danger-900/20',
    iconBg: 'bg-gradient-to-br from-danger-500 to-rose-600',
    glow: 'bg-gradient-to-br from-danger-400 to-pink-500',
    accent: 'text-danger-600 dark:text-danger-300',
  },
  neutral: {
    tint: 'bg-neutral-50',
    darkTint: 'dark:bg-neutral-800/60',
    iconBg: 'bg-gradient-to-br from-neutral-500 to-neutral-700',
    glow: 'bg-gradient-to-br from-neutral-400 to-neutral-600',
    accent: 'text-neutral-700 dark:text-neutral-200',
  },
};

function TrendPill({
  value,
  direction,
}: {
  value: string;
  direction: 'up' | 'down' | 'flat';
}) {
  const cls =
    direction === 'up'
      ? 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-300'
      : direction === 'down'
        ? 'bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-300'
        : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300';
  const arrow = direction === 'up' ? '▲' : direction === 'down' ? '▼' : '▬';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>
      <span className="text-[9px]">{arrow}</span>
      <span className="tabular-nums">{value}</span>
    </span>
  );
}

export function ReportKpiRow({ tiles }: { tiles: ReportKpiTile[] }) {
  if (tiles.length === 0) return null;
  const columns =
    tiles.length >= 4
      ? 'sm:grid-cols-2 lg:grid-cols-4'
      : tiles.length === 3
        ? 'sm:grid-cols-3'
        : 'sm:grid-cols-2';
  return (
    <div className={`grid grid-cols-1 gap-4 lg:gap-5 mb-6 ${columns}`}>
      {tiles.map((tile, idx) => {
        const tone = TONE[tile.tone];
        return (
          <div
            key={tile.id}
            className={`relative overflow-hidden rounded-xl ${tone.tint} ${tone.darkTint} shadow-md hover:shadow-lg transition-all duration-200 p-5 group animate-slide-up`}
            style={{ animationDelay: `${idx * 40}ms` }}
          >
            <div
              className={`absolute -top-10 -right-10 h-28 w-28 rounded-full ${tone.glow} opacity-20 group-hover:opacity-30 transition-opacity duration-300`}
            />
            <div className="relative flex items-center gap-3 mb-3">
              <div
                className={`w-10 h-10 rounded-lg ${tone.iconBg} flex items-center justify-center text-white shadow-sm`}
              >
                {tile.icon}
              </div>
              <span className="text-sm font-medium text-neutral-600 dark:text-neutral-300">{tile.title}</span>
            </div>
            <div className="relative flex items-baseline gap-2">
              <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{tile.value}</p>
              {tile.trend ? <TrendPill {...tile.trend} /> : null}
            </div>
            {tile.subLabel ? (
              <p className={`relative text-xs mt-1 font-medium ${tone.accent}`}>{tile.subLabel}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// ─── Shared small icon set used by report KPI tiles ─────────────────────────

export const KpiIcons = {
  rupee: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M6 11h12M9 19l6-8M6 15h8a4 4 0 000-8H6" />
    </svg>
  ),
  receipt: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12l3-2 3 2 3-2 3 2V7a2 2 0 00-2-2h-2M9 5V3h6v2" />
    </svg>
  ),
  cart: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293A1 1 0 005.414 17H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  trendUp: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  trendDown: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
    </svg>
  ),
  users: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  ),
  clock: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  percent: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 5L5 19M9 7a2 2 0 11-4 0 2 2 0 014 0zm10 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  tag: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5a2 2 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
    </svg>
  ),
  fire: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.24 17 7.25 17.73 8.855 18 10.23 18 12a8 8 0 01-.343 2.657z" />
    </svg>
  ),
  wallet: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M9 12h12m-3-3l3 3-3 3" />
    </svg>
  ),
  file: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  star: (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.7 5.225a1 1 0 00.95.69h5.494c.969 0 1.371 1.24.588 1.81l-4.448 3.232a1 1 0 00-.364 1.118l1.7 5.225c.3.921-.755 1.688-1.54 1.118l-4.449-3.232a1 1 0 00-1.175 0l-4.45 3.232c-.783.57-1.838-.197-1.54-1.118l1.7-5.225a1 1 0 00-.362-1.118L2.306 10.65c-.783-.57-.38-1.81.588-1.81h5.494a1 1 0 00.95-.69l1.7-5.225z" />
    </svg>
  ),
};
